import styled, { keyframes } from "styled-components";

import type { GeoPin } from "../api/types";
import { GEO_VIEW, projectToMap } from "../lib/geo";
import { theme } from "../theme";

// Stylized world silhouette from the design handoff (screens/map.jsx): 36×15
// lat/lon bands, X = land. Pins overlay via the equirectangular projection in
// lib/geo, whose latTop/latBottom describe this mask's crop.
const WORLD_MASK = [
  "....................................",
  "...XXX.XXXX.....XXXXXXXXXXXXX.......",
  "..XXXXXXXXXX...XXXXXXXXXXXXXXX......",
  "..XXXXXXXXX....XXXXXXXXXXXXXXX......",
  "..XXXXXXXX...XX.XXXXXXXXXXXXXX..X...",
  "...XXXXX....XX.XXXXXXXXXXXXX..XX....",
  "...XXXX.....XX.XXXXXXXXXXXX..XXX....",
  "....XX......XX..XXXXX.XXXX..XXX.....",
  "....XX.....XXX..XXXX..XX....XX......",
  "....XX.....XXX..XXX...........XX....",
  "....XXX....XXX...XX..........XXXX...",
  "....XXX.....XX...XX.........XXXX....",
  "....XX......X....................X..",
  ".....X..............................",
  "....................................",
];

const COLS = 36; // every WORLD_MASK row is this wide
const CELL = GEO_VIEW.w / COLS;

const DOTS: { x: number; y: number; land: boolean }[] = WORLD_MASK.flatMap((row, r) =>
  [...row].map((ch, c) => ({
    x: (c + 0.5) * CELL,
    y: (r + 0.5) * CELL,
    land: ch === "X",
  }))
);

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
`;

const pulse = keyframes`
  0% { transform: scale(0.6); opacity: 0.7; }
  70% { transform: scale(1.8); opacity: 0; }
  100% { transform: scale(1.8); opacity: 0; }
`;

const PulseRing = styled.circle`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${pulse} 2.4s ease-out infinite;
`;

/** Pin radius grows gently with the raccoon count (design: ~4–9px dots). */
function pinRadius(raccoons: number): number {
  return 2 + Math.min(2.5, Math.sqrt(raccoons) * 0.7);
}

export function WorldDotMap({
  pins,
  you,
}: {
  pins: GeoPin[];
  /** Viewer's pinned coordinates (from GeoStatusView.pinned); null/omitted hides the marker. */
  you?: { lat: number; lng: number } | null;
}) {
  const youPos = you ? projectToMap(you.lat, you.lng) : null;
  return (
    <Svg viewBox={`0 0 ${GEO_VIEW.w} ${GEO_VIEW.h}`}>
      {DOTS.map((d, i) => (
        <circle
          key={i}
          cx={d.x}
          cy={d.y}
          r={1.4}
          fill={d.land ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.04)"}
        />
      ))}

      {pins.map((p) => {
        // Country-only buckets carry no coordinates and produce no pin.
        if (p.lat === null || p.lng === null) return null;
        const { x, y } = projectToMap(p.lat, p.lng);
        const r = pinRadius(p.raccoons);
        return (
          <g key={`${p.countryCode}-${p.city}`}>
            <circle cx={x} cy={y} r={r * 2.2} fill="rgba(0, 152, 234, 0.18)" />
            <circle cx={x} cy={y} r={r} fill={theme.color.accent} />
          </g>
        );
      })}

      {youPos && (
        <g>
          <circle cx={youPos.x} cy={youPos.y} r={11} fill="rgba(0, 152, 234, 0.22)" />
          <PulseRing
            cx={youPos.x}
            cy={youPos.y}
            r={9}
            fill="none"
            stroke={theme.color.accent}
            strokeWidth={1.5}
          />
          <circle
            cx={youPos.x}
            cy={youPos.y}
            r={5}
            fill={theme.color.accent}
            stroke="#fff"
            strokeWidth={2}
          />
        </g>
      )}
    </Svg>
  );
}
